import { useCallback, useEffect, useRef, useState } from "react";
import { Send, Video, X, ShieldCheck, FileText, CalendarPlus, Upload, ImageIcon, LockKeyhole, FileImage } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { VideoCallModal } from "./VideoCallModal";

export function ConsultationChat({ consultation, currentRole, onClose, onConsultationUpdated }) {
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [pin, setPin] = useState("");
    const [unlocking, setUnlocking] = useState(false);
    const [loadingMsg, setLoadingMsg] = useState(false);
    const [showVideo, setShowVideo] = useState(false);
    const [note, setNote] = useState("");
    const [recommendation, setRecommendation] = useState("");
    const [appointmentAt, setAppointmentAt] = useState("");
    const [rxLabel, setRxLabel] = useState("");
    const [rxUploading, setRxUploading] = useState(false);
    const [zoomedRx, setZoomedRx] = useState(null);
    const [slipData, setSlipData] = useState(null);
    const [showSlip, setShowSlip] = useState(false);
    const scrollRef = useRef(null);
    const rxFileRef = useRef(null);

    const isDoctor = currentRole === "doctor";
    const isLocked = isDoctor && !consultation.vitals_access_granted;

    const [refreshTick, setRefreshTick] = useState(0);
    const refreshMessages = useCallback(() => setRefreshTick((x) => x + 1), []);

    useEffect(() => {
        if (isLocked) return undefined;
        let cancelled = false;
        const fetchMessages = async () => {
            try {
                const { data } = await api.get(`/consultations/${consultation.id}/messages`);
                if (!cancelled) setMessages(data);
            } catch {/* silent */}
        };
        fetchMessages();
        const t = setInterval(fetchMessages, 3500);
        return () => { cancelled = true; clearInterval(t); };
    }, [consultation.id, isLocked, refreshTick]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages.length]);

    const grantAccess = async (e) => {
        e?.preventDefault();
        if (pin.length !== 4) { toast.error("Enter the 4-digit PIN."); return; }
        setUnlocking(true);
        try {
            await api.post(`/consultations/${consultation.id}/grant-access`, { pin });
            toast.success("Access granted. You can now view this patient's file.");
            setPin("");
            onConsultationUpdated && onConsultationUpdated();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "PIN incorrect");
        } finally { setUnlocking(false); }
    };

    const send = async (e) => {
        e?.preventDefault();
        if (!text.trim()) return;
        setLoadingMsg(true);
        try {
            await api.post(`/consultations/${consultation.id}/messages`, { text });
            setText("");
            refreshMessages();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not send");
        } finally { setLoadingMsg(false); }
    };

    const addNote = async () => {
        if (!note.trim()) { toast.error("Write a note before saving."); return; }
        try {
            await api.post(`/consultations/${consultation.id}/notes`, {
                note, recommendation: recommendation || null,
                suggest_appointment_at: appointmentAt ? new Date(appointmentAt).toISOString() : null,
            });
            toast.success("Medical note added to patient record.");
            setNote(""); setRecommendation(""); setAppointmentAt("");
            onConsultationUpdated && onConsultationUpdated();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not save note");
        }
    };

    const onRxFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 4_000_000) { toast.error("Image too large (max 4MB)"); return; }
        const reader = new FileReader();
        reader.onload = async () => {
            setRxUploading(true);
            try {
                await api.post(`/consultations/${consultation.id}/prescriptions`, {
                    image_url: reader.result,
                    label: rxLabel || "Prescription",
                });
                toast.success("Prescription uploaded to patient file.");
                setRxLabel("");
                if (rxFileRef.current) rxFileRef.current.value = "";
                onConsultationUpdated && onConsultationUpdated();
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
            } finally { setRxUploading(false); }
        };
        reader.readAsDataURL(f);
    };

    const otherName = isDoctor ? consultation.patient_name : consultation.doctor_name;

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur grid place-items-center p-3 md:p-6" data-testid="consultation-chat" onClick={onClose}>
            <div className="bg-white rounded-3xl w-full max-w-5xl h-[90vh] flex overflow-hidden shadow-2xl relative" onClick={(e) => e.stopPropagation()}>

                {/* PIN-gate overlay for doctor */}
                {isLocked && (
                    <div className="absolute inset-0 z-30 bg-white/95 backdrop-blur grid place-items-center px-6" data-testid="pin-gate-overlay">
                        <form onSubmit={grantAccess} className="max-w-sm w-full text-center space-y-5">
                            <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white shadow-lg">
                                <LockKeyhole className="w-7 h-7" />
                            </div>
                            <div>
                                <h2 className="font-display text-3xl font-bold mb-2">Patient access locked</h2>
                                <p className="text-sm text-gray-600">
                                    To open the consultation with <strong>{consultation.patient_name}</strong>, ask the patient for
                                    their 4-digit access PIN and enter it below.
                                </p>
                            </div>
                            <input
                                type="text" inputMode="numeric" maxLength={4} autoFocus value={pin}
                                onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))}
                                placeholder="••••"
                                className="w-full px-4 py-4 rounded-2xl border-2 border-gray-200 focus:border-[var(--medx-orange)] outline-none text-center text-3xl font-mono tracking-[0.6em]"
                                data-testid="pin-gate-input"
                            />
                            <button type="submit" disabled={unlocking || pin.length !== 4} className="btn-primary w-full disabled:opacity-60" data-testid="pin-gate-submit">
                                {unlocking ? "Unlocking…" : "Unlock patient file"}
                            </button>
                            <button type="button" onClick={onClose} className="text-sm text-gray-500 hover:text-[var(--medx-red)]" data-testid="pin-gate-cancel">
                                Cancel — go back
                            </button>
                            <div className="flex items-start gap-2 text-xs text-gray-500 text-left bg-orange-50/50 border border-orange-100 rounded-xl p-3">
                                <ShieldCheck className="w-4 h-4 text-[var(--medx-orange)] mt-0.5 shrink-0" />
                                The PIN authorises one-time access to the patient&apos;s encrypted record (vitals, history, profile). All access is logged.
                            </div>
                        </form>
                    </div>
                )}

                {/* Left: chat */}
                <div className="flex-1 flex flex-col min-w-0">
                    <div className="flex items-center justify-between p-4 border-b border-gray-100">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white font-bold shrink-0">
                                {(otherName || "?").slice(0, 1).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                                <div className="font-display text-lg font-semibold truncate">{otherName}</div>
                                <div className="text-xs text-gray-500 truncate">
                                    {isDoctor ? "Patient" : consultation.doctor_speciality} · {consultation.status}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                            {isDoctor && !isLocked && (
                                <button
                                    onClick={async () => {
                                        try {
                                            const { data } = await api.get(`/consultations/${consultation.id}/patient-scan`);
                                            if (!data.has_scan) {
                                                toast.info("This patient has not uploaded a scan yet.");
                                                return;
                                            }
                                            setSlipData(data);
                                            setShowSlip(true);
                                        } catch (e) {
                                            toast.error(formatApiError(e.response?.data?.detail) || "Could not fetch slip");
                                        }
                                    }}
                                    className="px-3 py-2 rounded-full bg-white border border-gray-200 text-gray-700 text-sm font-semibold flex items-center gap-2 hover:bg-gray-50"
                                    data-testid="doctor-view-slip-btn"
                                    title="View patient's uploaded slip"
                                >
                                    <FileImage className="w-4 h-4" /> View slip
                                </button>
                            )}
                            <button onClick={() => setShowVideo(true)} disabled={isLocked} className="px-3 py-2 rounded-full bg-[var(--medx-orange)] text-white text-sm font-semibold flex items-center gap-2 hover:bg-[var(--medx-orange-vivid)] disabled:opacity-40" data-testid="start-video-btn">
                                <Video className="w-4 h-4" /> Start video
                            </button>
                            <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-gray-100 grid place-items-center" data-testid="close-chat">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-gradient-to-b from-orange-50/30 to-white" data-testid="chat-messages">
                        <div className="bg-white border border-gray-100 rounded-2xl p-4 max-w-md">
                            <div className="text-xs font-mono tracking-wider text-gray-500 mb-1">REPORTED PROBLEM</div>
                            <div className="text-sm text-gray-800">{consultation.problem}</div>
                        </div>
                        {messages.length === 0 && !isLocked && (
                            <div className="text-center text-xs text-gray-400 py-6 font-mono tracking-widest">Say hello to start the consult</div>
                        )}
                        {messages.map((m) => {
                            const mine = m.sender_role === currentRole;
                            return (
                                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                                    <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${mine ? "bg-[var(--medx-red)] text-white rounded-br-sm" : "bg-white border border-gray-100 text-gray-800 rounded-bl-sm"}`}>
                                        <div className="leading-relaxed whitespace-pre-wrap">{m.text}</div>
                                        <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-gray-400"}`}>
                                            {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Prescriptions visible to BOTH */}
                        {(consultation.prescriptions || []).length > 0 && (
                            <div className="space-y-2 pt-2">
                                <div className="text-xs font-mono tracking-wider text-gray-500 px-1">PRESCRIPTIONS &amp; DOCTOR&apos;S NOTES</div>
                                {consultation.prescriptions.map((p) => (
                                    <button key={p.id} onClick={() => setZoomedRx(p)} className="flex gap-3 p-3 bg-white border border-orange-100 rounded-2xl hover:border-[var(--medx-orange)] transition-all text-left w-full max-w-md" data-testid={`prescription-${p.id}`}>
                                        <img src={p.image_url} alt="prescription" className="w-16 h-16 rounded-lg object-cover border border-gray-100" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-display font-semibold text-gray-900 truncate">{p.label}</div>
                                            <div className="text-xs text-gray-500 truncate">From {p.doctor_name}</div>
                                            <div className="text-[10px] text-gray-400 font-mono mt-1">{new Date(p.created_at).toLocaleString()}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <form onSubmit={send} className="p-3 border-t border-gray-100 flex items-center gap-2 bg-white">
                        <input type="text" value={text} onChange={(e) => setText(e.target.value)}
                            placeholder={isLocked ? "Unlock with patient PIN first…" : "Type a message…"}
                            disabled={isLocked}
                            className="flex-1 px-4 py-3 rounded-full border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm disabled:bg-gray-50"
                            data-testid="chat-input" />
                        <button type="submit" disabled={loadingMsg || !text.trim() || isLocked} className="w-12 h-12 rounded-full bg-[var(--medx-red)] text-white grid place-items-center hover:bg-[var(--medx-red-deep)] disabled:opacity-50" data-testid="chat-send">
                            <Send className="w-4 h-4" />
                        </button>
                    </form>
                </div>

                {/* Right: side panel */}
                <aside className="hidden md:block w-80 border-l border-gray-100 bg-gray-50/50 overflow-y-auto">
                    {!isDoctor && (
                        <div className="p-5 border-b border-gray-100">
                            <div className="text-xs font-mono tracking-[0.22em] text-gray-500 mb-2 flex items-center gap-2">
                                <ShieldCheck className="w-3 h-3 text-[var(--medx-orange)]" /> ACCESS PIN
                            </div>
                            <div className="text-3xl font-mono font-bold tabular-nums text-[var(--medx-red)]" data-testid="patient-pin">{consultation.access_pin || "----"}</div>
                            <p className="text-xs text-gray-500 mt-2">
                                Share this 4-digit PIN with {otherName} so they can unlock your file. {consultation.vitals_access_granted ? "✓ Access granted" : "Not yet granted."}
                            </p>
                        </div>
                    )}
                    {isDoctor && (
                        <>
                            {consultation.vitals_access_granted && consultation.patient_vitals && consultation.patient_vitals.length > 0 && (
                                <div className="p-5 border-b border-gray-100" data-testid="patient-vitals-panel">
                                    <div className="text-xs font-mono tracking-[0.22em] text-gray-500 mb-3">LATEST VITALS</div>
                                    <div className="space-y-2 text-sm">
                                        {Object.entries({
                                            "BP": `${consultation.patient_vitals[0].blood_pressure_systolic}/${consultation.patient_vitals[0].blood_pressure_diastolic} mmHg`,
                                            "HR": `${consultation.patient_vitals[0].heart_rate} bpm`,
                                            "SpO₂": `${consultation.patient_vitals[0].spo2}%`,
                                            "Temp": `${consultation.patient_vitals[0].body_temperature}°C`,
                                            "BMI": consultation.patient_vitals[0].bmi,
                                            "Resp": `${consultation.patient_vitals[0].respiratory_rate}/min`,
                                            "Score": consultation.patient_vitals[0].health_score,
                                        }).map(([k, v]) => (
                                            <div key={k} className="flex justify-between border-b border-gray-100 pb-1">
                                                <span className="text-gray-500">{k}</span><span className="font-medium">{v}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="p-5 border-b border-gray-100">
                                <div className="text-xs font-mono tracking-[0.22em] text-gray-500 mb-3 flex items-center gap-2">
                                    <FileText className="w-3 h-3 text-[var(--medx-orange)]" /> ADD MEDICAL NOTE
                                </div>
                                <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What you observed…"
                                    className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm resize-none mb-2"
                                    data-testid="doctor-note-text" />
                                <input type="text" value={recommendation} onChange={(e) => setRecommendation(e.target.value)} placeholder="Recommendation (e.g. prescription)"
                                    className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm mb-2"
                                    data-testid="doctor-note-recommendation" />
                                <label className="text-xs text-gray-500 flex items-center gap-2 mb-1"><CalendarPlus className="w-3 h-3" /> Suggest in-office appointment</label>
                                <input type="datetime-local" value={appointmentAt} onChange={(e) => setAppointmentAt(e.target.value)}
                                    className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm mb-2"
                                    data-testid="doctor-note-appointment" />
                                <button onClick={addNote} className="btn-primary w-full !py-2.5 text-sm" data-testid="doctor-note-submit">Save to record</button>
                            </div>

                            <div className="p-5 border-b border-gray-100">
                                <div className="text-xs font-mono tracking-[0.22em] text-gray-500 mb-3 flex items-center gap-2">
                                    <ImageIcon className="w-3 h-3 text-[var(--medx-orange)]" /> UPLOAD PRESCRIPTION
                                </div>
                                <p className="text-xs text-gray-500 mb-2">Take a screenshot or photo of the script — drop it here. The patient sees it instantly.</p>
                                <input type="text" value={rxLabel} onChange={(e) => setRxLabel(e.target.value)} placeholder="Label (e.g. Amoxicillin 500mg)"
                                    className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm mb-2"
                                    data-testid="rx-label" />
                                <input ref={rxFileRef} type="file" accept="image/*" className="hidden" onChange={onRxFile} data-testid="rx-file" />
                                <button onClick={() => rxFileRef.current?.click()} disabled={rxUploading} className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full border-2 border-dashed border-[var(--medx-orange)]/40 text-sm font-semibold text-[var(--medx-red)] hover:bg-orange-50 disabled:opacity-60" data-testid="rx-upload-btn">
                                    <Upload className="w-4 h-4" /> {rxUploading ? "Uploading…" : "Upload prescription image"}
                                </button>
                            </div>
                        </>
                    )}

                    {(consultation.notes || []).length > 0 && (
                        <div className="p-5 border-t border-gray-100">
                            <div className="text-xs font-mono tracking-[0.22em] text-gray-500 mb-3">DOCTOR NOTES</div>
                            <div className="space-y-3">
                                {consultation.notes.map((n) => (
                                    <div key={n.id} className="bg-white rounded-xl p-3 border border-gray-100 text-sm">
                                        <div className="text-xs text-gray-400 mb-1">{new Date(n.created_at).toLocaleString()}</div>
                                        <div className="text-gray-800">{n.note}</div>
                                        {n.recommendation && <div className="text-[var(--medx-red)] text-xs mt-1"><strong>Rx:</strong> {n.recommendation}</div>}
                                        {n.suggest_appointment_at && <div className="text-[var(--medx-orange)] text-xs mt-1"><strong>Appt:</strong> {new Date(n.suggest_appointment_at).toLocaleString()}</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </aside>
            </div>

            <VideoCallModal
                open={showVideo}
                onClose={() => setShowVideo(false)}
                consultationId={consultation.id}
                remoteName={otherName}
                localName={isDoctor ? (consultation.doctor_name || "Doctor") : (consultation.patient_name || "Patient")}
                currentRole={isDoctor ? "doctor" : "patient"}
            />

            {/* Zoomed prescription */}
            {zoomedRx && (
                <div className="fixed inset-0 z-[70] bg-black/90 grid place-items-center p-6" onClick={() => setZoomedRx(null)} data-testid="rx-zoom-modal">
                    <button className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center" data-testid="rx-zoom-close">
                        <X className="w-5 h-5" />
                    </button>
                    <div className="max-w-3xl max-h-full text-white text-center space-y-3" onClick={(e) => e.stopPropagation()}>
                        <div className="text-sm font-mono tracking-widest text-orange-200">{zoomedRx.label}</div>
                        <img src={zoomedRx.image_url} alt="prescription" className="max-h-[75vh] mx-auto rounded-2xl shadow-2xl" />
                        <div className="text-xs text-white/60">From {zoomedRx.doctor_name} · {new Date(zoomedRx.created_at).toLocaleString()}</div>
                        <a href={zoomedRx.image_url} download={`prescription-${zoomedRx.id}.png`} className="inline-block px-5 py-2 rounded-full bg-[var(--medx-orange)] text-white text-sm font-semibold hover:bg-[var(--medx-orange-vivid)]" data-testid="rx-download">
                            Download
                        </a>
                    </div>
                </div>
            )}
            {showSlip && slipData && (
                <div className="fixed inset-0 z-[70] bg-black/90 grid place-items-center p-4" onClick={() => setShowSlip(false)} data-testid="slip-view-modal">
                    <button onClick={() => setShowSlip(false)} className="absolute top-5 right-5 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center" data-testid="slip-view-close">
                        <X className="w-5 h-5" />
                    </button>
                    <div className="max-w-5xl w-full max-h-[90vh] bg-white rounded-2xl overflow-hidden flex flex-col md:flex-row" onClick={(e) => e.stopPropagation()}>
                        <div className="md:w-1/2 bg-gray-100 overflow-auto grid place-items-center p-4">
                            <img src={`data:${slipData.mime_type || "image/jpeg"};base64,${slipData.image_base64}`}
                                alt="Original slip" className="max-w-full max-h-[85vh] rounded-lg shadow"
                                data-testid="slip-view-image" />
                        </div>
                        <div className="md:w-1/2 p-5 overflow-y-auto">
                            <div className="text-[10px] font-mono tracking-widest text-[var(--medx-red)] mb-2">PATIENT'S ORIGINAL SLIP</div>
                            <h3 className="font-display text-xl font-bold mb-1">OCR-extracted vitals</h3>
                            <div className="text-xs text-gray-500 mb-4">Uploaded {new Date(slipData.created_at).toLocaleString()}</div>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                {Object.entries(slipData.vitals || {}).filter(([k, v]) => typeof v === "number" && v !== null && !k.startsWith("_")).map(([k, v]) => (
                                    <div key={k} className="bg-gray-50 rounded-lg p-2">
                                        <div className="text-[10px] font-mono text-gray-500 capitalize">{k.replace(/_/g, " ")}</div>
                                        <div className="font-semibold">{v}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
